import api from './api';

/**
 * Tremendous Connect Service
 * Handles API calls for Tremendous Connect integration
 */
const tremendousConnectService = {
  /**
   * Start Tremendous Connect onboarding for an organization
   * @param {string} organizationId - Organization ID
   * @param {string} adminEmail - Admin's email for onboarding
   * @param {string} adminName - Admin's name for onboarding
   * @param {string} returnUrl - URL to return to after onboarding (optional)
   * @returns {Promise<object>} - { session_url, tremendous_org_id, tremendous_member_id, status }
   */
  startConnect: async (organizationId, adminEmail, adminName, returnUrl = null) => {
    const response = await api.post('/api/v1/tremendous/connect/start', {
      organization_id: organizationId,
      admin_email: adminEmail,
      admin_name: adminName,
      return_url: returnUrl || window.location.href,
    });
    return response.data;
  },

  /**
   * Get connection status for an organization
   * @param {string} organizationId - Organization ID
   * @returns {Promise<object>} - { organization_id, status, is_connected, tremendous_org_id }
   */
  getStatus: async (organizationId) => {
    const response = await api.get(`/api/v1/tremendous/connect/status/${organizationId}`);
    return response.data;
  },

  /**
   * Generate a dashboard session URL for an organization
   * @param {string} organizationId - Organization ID
   * @param {string} returnUrl - URL to return to after dashboard session (optional)
   * @returns {Promise<object>} - { session_url }
   */
  getDashboardSession: async (organizationId, returnUrl = null) => {
    const response = await api.post(`/api/v1/tremendous/connect/session/${organizationId}`, null, {
      params: { return_url: returnUrl || window.location.href },
    });
    return response.data;
  },

  /**
   * Get the Tremendous balance for an organization
   * @param {string} organizationId - Organization ID
   * @returns {Promise<object>} - { balance, currency }
   */
  getBalance: async (organizationId) => {
    const response = await api.get(`/api/v1/tremendous/connect/balance/${organizationId}`);
    return response.data;
  },

  // ---- Campaigns ----

  listCampaigns: async (organizationId) => {
    const response = await api.get(`/api/v1/tremendous/campaigns/${organizationId}`);
    return response.data;
  },

  createCampaign: async (organizationId, data) => {
    const response = await api.post(`/api/v1/tremendous/campaigns/${organizationId}`, data);
    return response.data;
  },

  getCampaign: async (organizationId, campaignId) => {
    const response = await api.get(`/api/v1/tremendous/campaigns/${organizationId}/${campaignId}`);
    return response.data;
  },

  updateCampaign: async (organizationId, campaignId, data) => {
    const response = await api.put(`/api/v1/tremendous/campaigns/${organizationId}/${campaignId}`, data);
    return response.data;
  },

  syncCampaignToMarketplace: async (organizationId, campaignId) => {
    const response = await api.post(`/api/v1/tremendous/campaigns/${organizationId}/${campaignId}/sync`);
    return response.data;
  },

  // ---- Products ----

  listProducts: async (organizationId) => {
    const response = await api.get(`/api/v1/tremendous/products/${organizationId}`);
    return response.data;
  },
};

export default tremendousConnectService;
